import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI } from '../../api/orders';
import { formatCurrency, formatDateTime, formatPhone } from '../../utils/formatters';
import { NotificationContext } from '../../context/NotificationContext';
import Button from '../common/Button';
import Modal from '../common/Modal';
import './OrderManagement.css';

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'all',         label: 'All Orders',    icon: '📋', status: null },
  { id: 'pending',     label: 'Pending',        icon: '⏳', status: 'pending' },
  { id: 'in_progress', label: 'In Progress',    icon: '🧺', status: 'in_progress' },
  { id: 'ready',       label: 'Ready',          icon: '✅', status: 'ready' },
  { id: 'completed',   label: 'Completed',      icon: '📦', status: 'completed' },
  { id: 'cancelled',   label: 'Cancelled',      icon: '❌', status: 'cancelled' },
];

const STATUS_META = {
  pending:     { label: 'Pending',     bg: '#fef3c7', color: '#92400e' },
  in_progress: { label: 'In Progress', bg: '#dbeafe', color: '#1e40af' },
  ready:       { label: 'Ready',       bg: '#d1fae5', color: '#065f46' },
  completed:   { label: 'Completed',   bg: '#ede9fe', color: '#4c1d95' },
  cancelled:   { label: 'Cancelled',   bg: '#fee2e2', color: '#991b1b' },
};

const STATUS_FLOW = {
  pending:     ['in_progress', 'cancelled'],
  in_progress: ['ready', 'cancelled'],
  ready:       ['completed', 'in_progress'],
  completed:   [],
  cancelled:   ['pending'],
};

const PAYMENT_META = {
  paid:      { label: 'Paid',      bg: '#d1fae5', color: '#065f46' },
  pending:   { label: 'Unpaid',    bg: '#fef3c7', color: '#92400e' },
  on_credit: { label: 'On Credit', bg: '#fde8bb', color: '#92400e' },
  partial:   { label: 'Partial',   bg: '#dbeafe', color: '#1e40af' },
};

const DELIVERY_META = {
  standard: { label: 'Standard',    bg: '#f1f5f9', color: '#475569' },
  express:  { label: '⚡ Express',   bg: '#fef3c7', color: '#92400e' },
};

const METHOD_META = {
  cash:                 { label: 'Cash',         color: '#10b981' },
  card:                 { label: 'Card',          color: '#3b82f6' },
  wallet:               { label: 'Wallet',        color: '#8b5cf6' },
  on_credit:            { label: 'Credit',        color: '#f59e0b' },
  subscription_credit:  { label: 'Subscription',  color: '#06b6d4' },
};

// ─── Inline components ────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, bg: '#f3f4f6', color: '#374151' };
  return <span className="om-badge" style={{ background: m.bg, color: m.color }}>{m.label}</span>;
};

const PayBadge = ({ status }) => {
  const m = PAYMENT_META[status] || { label: status, bg: '#f3f4f6', color: '#374151' };
  return <span className="om-badge" style={{ background: m.bg, color: m.color }}>{m.label}</span>;
};

const SummaryCard = ({ icon, label, value, sub, accent }) => (
  <div className="ps-summary-card" style={{ borderTopColor: accent }}>
    <div className="ps-summary-icon">{icon}</div>
    <div>
      <div className="ps-summary-value" style={{ color: accent }}>{value}</div>
      <div className="ps-summary-label">{label}</div>
      {sub && <div className="ps-summary-sub">{sub}</div>}
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const OrderManagement = () => {
  const navigate  = useNavigate();
  const { showNotification } = useContext(NotificationContext);

  // Tab + list state
  const [activeTab, setActiveTab]   = useState('all');
  const [orders, setOrders]         = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage]             = useState(1);
  const [pages, setPages]           = useState(1);
  const [loading, setLoading]       = useState(false);

  // Filters
  const [search, setSearch]               = useState('');
  const [filterPayment, setFilterPayment] = useState('');
  const [filterDelivery, setFilterDelivery] = useState('');
  const [filterStart, setFilterStart]     = useState('');
  const [filterEnd, setFilterEnd]         = useState('');

  // Summary
  const [summary, setSummary] = useState(null);

  // Detail modal
  const [selectedOrder, setSelectedOrder]   = useState(null);
  const [detailLoading, setDetailLoading]   = useState(false);

  // Status modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusTarget, setStatusTarget]       = useState(null);
  const [newStatus, setNewStatus]             = useState('');
  const [statusLoading, setStatusLoading]     = useState(false);

  // Delete confirm
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [deleteLoading, setDeleteLoading]     = useState(false);

  // ── Load summary ────────────────────────────────────────────────────────────
  const loadSummary = useCallback(async () => {
    try {
      const res = await ordersAPI.getDashboardSummary();
      setSummary(res.data);
    } catch { /* non-critical */ }
  }, []);

  // ── Load orders ─────────────────────────────────────────────────────────────
  const loadOrders = useCallback(async (resetPage = false) => {
    setLoading(true);
    const p = resetPage ? 1 : page;
    if (resetPage) setPage(1);
    try {
      const params = {
        page: p, limit: 20,
        search: search || undefined,
        paymentStatus: filterPayment || undefined,
        deliveryOption: filterDelivery || undefined,
        startDate: filterStart || undefined,
        endDate: filterEnd || undefined,
      };
      const tabStatus = TABS.find(t => t.id === activeTab)?.status;
      if (tabStatus) params.status = tabStatus;

      const res = await ordersAPI.getAll(params);
      setOrders(res.data);
      setTotalCount(res.pagination.total);
      setPages(res.pagination.pages);
    } catch {
      showNotification('Failed to load orders', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, search, filterPayment, filterDelivery, filterStart, filterEnd]);

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { loadOrders(true); }, [activeTab, search, filterPayment, filterDelivery, filterStart, filterEnd]);
  useEffect(() => { if (page > 0) loadOrders(); }, [page]);

  // ── Open detail ─────────────────────────────────────────────────────────────
  const openDetail = async (order) => {
    setSelectedOrder(order);
    setDetailLoading(true);
    try {
      const res = await ordersAPI.getById(order._id);
      setSelectedOrder(res.data);
    } catch {
      showNotification('Failed to load order details', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Open status modal ────────────────────────────────────────────────────────
  const openStatusModal = (order, e) => {
    e?.stopPropagation();
    setStatusTarget(order);
    setNewStatus(order.status);
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {
    setStatusLoading(true);
    try {
      await ordersAPI.updateStatus(statusTarget._id, newStatus);
      showNotification('Status updated', 'success');
      setShowStatusModal(false);
      setStatusTarget(null);
      loadOrders();
      loadSummary();
      if (selectedOrder?._id === statusTarget._id) {
        const res = await ordersAPI.getById(statusTarget._id);
        setSelectedOrder(res.data);
      }
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to update status', 'error');
    } finally {
      setStatusLoading(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await ordersAPI.delete(deleteTarget._id);
      showNotification('Order deleted', 'success');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      if (selectedOrder?._id === deleteTarget._id) setSelectedOrder(null);
      loadOrders();
      loadSummary();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to delete order', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Print receipt ────────────────────────────────────────────────────────────
  const handlePrintReceipt = async (orderId) => {
    try {
      const res = await ordersAPI.getReceipt(orderId);
      const receipt = res.data;
      const win = window.open('', '_blank');
      win.document.write(generateReceiptHTML(receipt));
      win.document.close();
      win.focus();
      setTimeout(() => { win.print(); win.close(); }, 300);
    } catch {
      showNotification('Failed to load receipt', 'error');
    }
  };

  const generateReceiptHTML = (r) => `
    <!DOCTYPE html><html><head><title>Receipt ${r.orderNumber}</title>
    <style>
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family:'Courier New',monospace; width:80mm; padding:8mm; font-size:12px; }
      .center { text-align:center; } .bold { font-weight:bold; }
      .header { border-bottom:2px dashed #000; padding-bottom:4mm; margin-bottom:4mm; }
      .co-name { font-size:16px; font-weight:bold; }
      .section { margin:4mm 0; }
      .section-title { font-weight:bold; border-bottom:1px solid #000; margin-bottom:2mm; }
      .row { display:flex; justify-content:space-between; margin:1mm 0; }
      table { width:100%; border-collapse:collapse; }
      th,td { text-align:left; padding:1mm 0; font-size:11px; }
      th { border-bottom:1px solid #000; }
      .total-section { border-top:2px solid #000; margin-top:4mm; padding-top:3mm; }
      .total-row { display:flex; justify-content:space-between; font-size:14px; font-weight:bold; }
      .footer { text-align:center; margin-top:8mm; border-top:2px dashed #000; padding-top:4mm; font-size:11px; }
    </style></head><body>
    <div class="header center">
      <div class="co-name">Smart Laundry</div>
      <div>Kuwait City, Kuwait · 9999 8888</div>
      <div class="bold" style="margin-top:3mm">Order: ${r.orderNumber}</div>
      <div>${new Date(r.orderDate).toLocaleString('en-KW')}</div>
    </div>
    <div class="section">
      <div class="section-title">CUSTOMER</div>
      <div>${r.customer.name}</div><div>${r.customer.phone}</div>
    </div>
    <div class="section">
      <div class="section-title">ITEMS</div>
      <table><thead><tr><th>Item</th><th>Qty</th><th>Total</th></tr></thead><tbody>
      ${r.items.map(i => `<tr><td>${i.name}</td><td>×${i.quantity}</td><td>KD ${i.total.toFixed(3)}</td></tr>`).join('')}
      </tbody></table>
    </div>
    <div class="section">
      <div class="row"><span>Subtotal</span><span>KD ${r.pricing.subtotal.toFixed(3)}</span></div>
      ${r.pricing.taxAmount > 0 ? `<div class="row"><span>Tax</span><span>KD ${r.pricing.taxAmount.toFixed(3)}</span></div>` : ''}
      ${r.pricing.addon > 0 ? `<div class="row"><span>Addon</span><span>KD ${r.pricing.addon.toFixed(3)}</span></div>` : ''}
      ${r.pricing.deliveryCharge > 0 ? `<div class="row"><span>Delivery</span><span>KD ${r.pricing.deliveryCharge.toFixed(3)}</span></div>` : ''}
      ${r.pricing.discount > 0 ? `<div class="row"><span>Discount</span><span>-KD ${r.pricing.discount.toFixed(3)}</span></div>` : ''}
      ${r.pricing.subscriptionDiscount > 0 ? `<div class="row"><span>Sub Discount</span><span>-KD ${r.pricing.subscriptionDiscount.toFixed(3)}</span></div>` : ''}
    </div>
    ${r.subscription ? `<div style="background:#f0f0f0;padding:2mm;text-align:center;margin:3mm 0;">⭐ ${r.subscription.name} — On Credit</div>` : ''}
    <div class="total-section">
      <div class="total-row"><span>TOTAL</span><span>KD ${r.pricing.totalAmount.toFixed(3)}</span></div>
      ${r.pricing.walletAmountUsed > 0 ? `<div class="row"><span>Wallet Used</span><span>-KD ${r.pricing.walletAmountUsed.toFixed(3)}</span></div>` : ''}
      ${r.pricing.creditAmountUsed > 0 ? `<div class="row"><span>On Credit</span><span>KD ${r.pricing.creditAmountUsed.toFixed(3)}</span></div>` : ''}
      <div class="row bold"><span>Collected</span><span>KD ${r.pricing.finalAmount.toFixed(3)}</span></div>
    </div>
    <div class="footer">
      Thank you for choosing Smart Laundry!<br>
      Expected: ${new Date(r.expectedDelivery).toLocaleDateString('en-KW')}<br>
      Delivery: ${r.deliveryOption === 'express' ? '⚡ Express' : 'Standard'}
    </div>
    </body></html>`;

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const tabCount = (statusKey) => {
    if (!summary?.byStatus || !statusKey) return null;
    return summary.byStatus.find(s => s._id === statusKey)?.count ?? 0;
  };

  const clearFilters = () => {
    setFilterPayment(''); setFilterDelivery(''); setFilterStart(''); setFilterEnd('');
  };

  const hasFilters = filterPayment || filterDelivery || filterStart || filterEnd;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="ps-root">

      {/* ── Header ── */}
      <div className="ps-header">
        <div>
          <h1 className="ps-title">Order Management</h1>
          <p className="ps-subtitle">Track, manage and update all laundry orders</p>
        </div>
        <Button variant="primary" onClick={() => navigate('/pos')}>+ New Order</Button>
      </div>

      {/* ── Summary Cards ── */}
      {summary && (
        <div className="ps-summary-row">
          <SummaryCard
            icon="📋" label="Today's Orders" accent="#3b82f6"
            value={summary.today?.count ?? 0}
            sub={`${formatCurrency(summary.today?.revenue ?? 0)} revenue`}
          />
          <SummaryCard
            icon="⏳" label="Pending" accent="#f59e0b"
            value={tabCount('pending') ?? 0}
            sub="awaiting processing"
          />
          <SummaryCard
            icon="🧺" label="In Progress" accent="#6366f1"
            value={tabCount('in_progress') ?? 0}
            sub="currently cleaning"
          />
          <SummaryCard
            icon="✅" label="Ready" accent="#10b981"
            value={tabCount('ready') ?? 0}
            sub="awaiting pickup"
          />
          <SummaryCard
            icon="🏦" label="On Credit" accent="#ef4444"
            value={summary.onCredit?.count ?? 0}
            sub={formatCurrency(summary.onCredit?.total ?? 0)}
          />
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="ps-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`ps-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => { setActiveTab(tab.id); setPage(1); }}
          >
            <span className="ps-tab-icon">{tab.icon}</span>
            {tab.label}
            {tab.status && tabCount(tab.status) != null && (
              <span className="om-tab-count">{tabCount(tab.status)}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="ps-content">
        <div className="ps-tab-pane">

          {/* Filters */}
          <div className="ps-filters">
            <div className="ps-search-wrap">
              <span className="ps-search-icon">🔍</span>
              <input
                className="ps-search-input"
                placeholder="Search by order #, customer name or phone…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="om-search-clear" onClick={() => setSearch('')}>✕</button>
              )}
            </div>
            <select className="ps-filter-select" value={filterPayment} onChange={e => setFilterPayment(e.target.value)}>
              <option value="">All Payments</option>
              <option value="paid">Paid</option>
              <option value="pending">Unpaid</option>
              <option value="on_credit">On Credit</option>
              <option value="partial">Partial</option>
            </select>
            <select className="ps-filter-select" value={filterDelivery} onChange={e => setFilterDelivery(e.target.value)}>
              <option value="">All Delivery</option>
              <option value="standard">Standard</option>
              <option value="express">Express</option>
            </select>
            <input type="date" className="ps-filter-date" value={filterStart} onChange={e => setFilterStart(e.target.value)} />
            <span style={{ color: '#94a3b8', fontSize: 13 }}>→</span>
            <input type="date" className="ps-filter-date" value={filterEnd} onChange={e => setFilterEnd(e.target.value)} />
            {hasFilters && (
              <button className="om-clear-filters" onClick={clearFilters}>Clear filters ✕</button>
            )}
            <Button variant="secondary" onClick={() => { loadOrders(); loadSummary(); }}>↺</Button>
          </div>

          {/* Table meta */}
          <div className="ps-table-meta">
            <span className="ps-count">{totalCount} order{totalCount !== 1 ? 's' : ''}</span>
            {summary?.today?.revenue != null && activeTab === 'all' && (
              <span>Today's revenue: <strong style={{ color: '#10b981' }}>{formatCurrency(summary.today.revenue)}</strong></span>
            )}
          </div>

          {/* Table */}
          {loading ? (
            <div className="ps-loading">Loading orders…</div>
          ) : (
            <>
              <div className="ps-table-wrap">
                <table className="ps-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Customer</th>
                      <th>Items</th>
                      <th>Method</th>
                      <th>Total</th>
                      <th>Payment</th>
                      <th>Delivery</th>
                      <th>Status</th>
                      <th>Expected</th>
                      <th>Created</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="ps-empty">
                          <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>
                          No orders found
                        </td>
                      </tr>
                    ) : orders.map(order => (
                      <tr key={order._id} className="ps-row" onClick={() => openDetail(order)}>
                        <td>
                          <span className="ps-order-num">{order.orderNumber}</span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="om-avatar-sm">
                              {order.customer?.name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="om-cust-name-cell">{order.customer?.name}</div>
                              <div className="om-cust-phone-cell">{formatPhone(order.customer?.phone)}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ fontWeight: 600, color: '#374151' }}>
                          {order.items?.length} item{order.items?.length !== 1 ? 's' : ''}
                        </td>
                        <td>
                          <span style={{
                            fontSize: 12, fontWeight: 700,
                            color: METHOD_META[order.paymentMethod]?.color || '#374151'
                          }}>
                            {METHOD_META[order.paymentMethod]?.label || order.paymentMethod}
                          </span>
                        </td>
                        <td style={{ fontWeight: 800, color: '#0f172a' }}>
                          {formatCurrency(order.totalAmount)}
                        </td>
                        <td><PayBadge status={order.paymentStatus} /></td>
                        <td>
                          <span className="om-badge" style={{
                            background: DELIVERY_META[order.deliveryOption]?.bg,
                            color: DELIVERY_META[order.deliveryOption]?.color
                          }}>
                            {DELIVERY_META[order.deliveryOption]?.label}
                          </span>
                        </td>
                        <td><StatusBadge status={order.status} /></td>
                        <td className="ps-date">
                          {order.expectedDelivery
                            ? new Date(order.expectedDelivery).toLocaleDateString('en-KW')
                            : '—'}
                        </td>
                        <td className="ps-date">{formatDateTime(order.createdAt)}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              className="om-action-btn"
                              onClick={e => openStatusModal(order, e)}
                              title="Update status"
                            >
                              ✏️
                            </button>
                            <button
                              className="om-action-btn"
                              onClick={e => { e.stopPropagation(); handlePrintReceipt(order._id); }}
                              title="Print receipt"
                            >
                              🖨️
                            </button>
                            {order.status === 'pending' && (
                              <button
                                className="om-action-btn danger"
                                onClick={e => { e.stopPropagation(); setDeleteTarget(order); setShowDeleteModal(true); }}
                                title="Delete order"
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pages > 1 && (
                <div className="ps-pagination">
                  <Button variant="secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</Button>
                  <span className="ps-page-label">Page {page} of {pages}</span>
                  <Button variant="secondary" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          ORDER DETAIL MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      {selectedOrder && (
        <Modal
          isOpen={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
          title={`Order ${selectedOrder.orderNumber}`}
          size="large"
        >
          {detailLoading ? (
            <div className="ps-loading">Loading details…</div>
          ) : (
            <OrderDetailContent
              order={selectedOrder}
              onStatusUpdate={(e) => openStatusModal(selectedOrder, e)}
              onPrint={() => handlePrintReceipt(selectedOrder._id)}
              formatCurrency={formatCurrency}
              formatDateTime={formatDateTime}
              formatPhone={formatPhone}
              STATUS_META={STATUS_META}
              PAYMENT_META={PAYMENT_META}
              DELIVERY_META={DELIVERY_META}
              METHOD_META={METHOD_META}
            />
          )}
        </Modal>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          STATUS UPDATE MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => { setShowStatusModal(false); setStatusTarget(null); }}
        title="Update Order Status"
      >
        {statusTarget && (
          <div className="ps-modal-form">
            <div className="om-status-modal-info">
              <span className="ps-order-num">{statusTarget.orderNumber}</span>
              <span style={{ color: '#64748b', fontSize: 13 }}>{statusTarget.customer?.name}</span>
              <StatusBadge status={statusTarget.status} />
            </div>

            <div className="ps-modal-field">
              <label>Select new status</label>
              <div className="om-status-grid">
                {Object.entries(STATUS_META).map(([key, meta]) => {
                  const allowed = STATUS_FLOW[statusTarget.status] || [];
                  const isCurrent = key === statusTarget.status;
                  const isAllowed = allowed.includes(key);
                  return (
                    <button
                      key={key}
                      className={`om-status-option${newStatus === key ? ' selected' : ''}`}
                      style={{
                        ...(newStatus === key ? { background: meta.bg, borderColor: meta.color, color: meta.color } : {}),
                        opacity: isCurrent ? 0.45 : isAllowed ? 1 : 0.4,
                        cursor: isCurrent ? 'default' : isAllowed ? 'pointer' : 'not-allowed'
                      }}
                      onClick={() => { if (isAllowed) setNewStatus(key); }}
                      title={isCurrent ? 'Current status' : !isAllowed ? 'Not allowed from current status' : ''}
                    >
                      {meta.label}
                      {isCurrent && <span style={{ fontSize: 10, display: 'block', opacity: 0.7 }}>current</span>}
                    </button>
                  );
                })}
              </div>
              <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                Only valid next-step transitions are enabled.
              </p>
            </div>

            <div className="ps-modal-actions">
              <Button variant="secondary" onClick={() => { setShowStatusModal(false); setStatusTarget(null); }}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleStatusUpdate}
                disabled={newStatus === statusTarget.status || statusLoading}
              >
                {statusLoading ? 'Updating…' : 'Update Status'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ══════════════════════════════════════════════════════════════════════
          DELETE CONFIRM MODAL
          ══════════════════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
        title="Delete Order"
      >
        {deleteTarget && (
          <div className="ps-modal-form">
            <div className="om-delete-warning">
              <span style={{ fontSize: 32 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>
                  Delete order <span style={{ fontFamily: 'monospace' }}>{deleteTarget.orderNumber}</span>?
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  This will permanently remove the order for <strong>{deleteTarget.customer?.name}</strong>. This action cannot be undone.
                </div>
              </div>
            </div>
            <div className="ps-modal-actions">
              <Button variant="secondary" onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
                {deleteLoading ? 'Deleting…' : 'Delete Order'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ─── Order Detail Content (extracted for clarity) ─────────────────────────────
const OrderDetailContent = ({
  order, onStatusUpdate, onPrint,
  formatCurrency, formatDateTime, formatPhone,
  STATUS_META, PAYMENT_META, DELIVERY_META, METHOD_META
}) => {
  const sm = STATUS_META[order.status] || {};
  const pm = PAYMENT_META[order.paymentStatus] || {};
  const dm = DELIVERY_META[order.deliveryOption] || {};

  const hasSub = !!order.subscriptionApplied;

  return (
    <div className="om-detail">

      {/* Top bar: badges + actions */}
      <div className="om-detail-top-bar">
        <div className="om-detail-badges">
          <span className="om-badge" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
          <span className="om-badge" style={{ background: pm.bg, color: pm.color }}>{pm.label}</span>
          <span className="om-badge" style={{ background: dm.bg, color: dm.color }}>{dm.label}</span>
          {order.paymentMethod && (
            <span className="om-badge" style={{ background: '#f1f5f9', color: METHOD_META[order.paymentMethod]?.color || '#374151' }}>
              {METHOD_META[order.paymentMethod]?.label}
            </span>
          )}
          {hasSub && (
            <span className="om-badge" style={{ background: '#ede9fe', color: '#5b21b6' }}>⭐ Subscription</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="om-icon-btn" onClick={onPrint} title="Print receipt">🖨️ Print</button>
          <button className="ps-settle-btn-lg" onClick={onStatusUpdate}>Update Status</button>
        </div>
      </div>

      {/* Two-column: Order info + Customer */}
      <div className="om-detail-2col">

        {/* Order info */}
        <div className="om-detail-section">
          <div className="om-section-title">Order Information</div>
          {[
            ['Order Number',      <span className="ps-order-num">{order.orderNumber}</span>],
            ['Order Date',        formatDateTime(order.createdAt)],
            ['Expected Delivery', order.expectedDelivery ? new Date(order.expectedDelivery).toLocaleDateString('en-KW') : '—'],
            ['Delivery Option',   dm.label],
            ['Created By',        order.createdBy?.name || '—'],
            ...(order.updatedBy ? [['Last Updated By', order.updatedBy?.name]] : []),
            ...(order.deliveryDate ? [['Delivered On', formatDateTime(order.deliveryDate)]] : []),
          ].map(([label, val]) => (
            <div key={label} className="om-info-row">
              <span className="om-info-label">{label}</span>
              <span className="om-info-val">{val}</span>
            </div>
          ))}
          {order.notes && (
            <div className="om-notes-box">
              <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</span>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#374151' }}>{order.notes}</p>
            </div>
          )}
        </div>

        {/* Customer */}
        <div className="om-detail-section">
          <div className="om-section-title">Customer</div>
          <div className="om-customer-card">
            <div className="om-avatar-lg">{order.customer?.name?.charAt(0).toUpperCase()}</div>
            <div className="om-customer-info">
              <div className="om-cust-name-lg">{order.customer?.name}</div>
              <div className="om-cust-detail">📞 {formatPhone(order.customer?.phone)}</div>
              {order.customer?.email && <div className="om-cust-detail">✉️ {order.customer.email}</div>}
              {order.customer?.address?.area && (
                <div className="om-cust-detail">
                  📍 {[
                    order.customer.address.area,
                    order.customer.address.block && `Block ${order.customer.address.block}`,
                    order.customer.address.street,
                    order.customer.address.building && `Bldg ${order.customer.address.building}`,
                  ].filter(Boolean).join(', ')}
                </div>
              )}
            </div>
          </div>

          {/* Wallet / credit chips */}
          {(order.customer?.walletBalance > 0 || order.customer?.creditLimit > 0) && (
            <div className="om-finance-chips">
              {order.customer?.walletBalance > 0 && (
                <span className="om-finance-chip green">
                  💰 Wallet: {formatCurrency(order.customer.walletBalance)}
                </span>
              )}
              {order.customer?.creditLimit > 0 && (
                <span className="om-finance-chip amber">
                  🏦 Credit: {formatCurrency(Math.max(0, order.customer.creditLimit - (order.customer.creditUsed || 0)))} avail.
                </span>
              )}
              {order.customer?.loyaltyPoints > 0 && (
                <span className="om-finance-chip purple">
                  ⭐ {order.customer.loyaltyPoints} pts
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Items table */}
      <div className="om-detail-section">
        <div className="om-section-title">Items ({order.items?.length})</div>
        <div className="ps-table-wrap">
          <table className="ps-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Details</th>
                <th style={{ textAlign: 'center' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>Unit Price</th>
                <th style={{ textAlign: 'right' }}>Subtotal</th>
                {order.taxAmount > 0 && <th style={{ textAlign: 'right' }}>Tax</th>}
                <th style={{ textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((item, i) => {
                const d = item.itemDetails;
                const hasTags = d && (d.brand || d.colors?.length || d.damages?.length || d.stains?.length || d.specialInstructions);
                return (
                  <tr key={i} className="ps-row" style={{ cursor: 'default' }}>
                    <td style={{ fontWeight: 700, color: '#0f172a' }}>{item.service?.name || '—'}</td>
                    <td>
                      {hasTags ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {d.brand && <span className="om-item-tag">{d.brand}</span>}
                          {d.colors?.map(c => <span key={c} className="om-item-tag">{c}</span>)}
                          {d.damages?.length > 0 && <span className="om-item-tag warn">⚠️ {d.damages.length} dmg</span>}
                          {d.stains?.length > 0 && <span className="om-item-tag warn">💧 {d.stains.length} stain</span>}
                          {d.specialInstructions && (
                            <span className="om-item-tag" title={d.specialInstructions}>📝 note</span>
                          )}
                        </div>
                      ) : <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className="om-qty-chip">×{item.quantity}</span>
                    </td>
                    <td style={{ textAlign: 'right', color: '#64748b' }}>{formatCurrency(item.price)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.subtotal)}</td>
                    {order.taxAmount > 0 && (
                      <td style={{ textAlign: 'right', color: '#94a3b8', fontSize: 12 }}>
                        {formatCurrency(item.tax)}
                      </td>
                    )}
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatCurrency(item.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment breakdown */}
      <div className="om-detail-2col">
        <div className="om-detail-section">
          <div className="om-section-title">Payment Breakdown</div>
          <div className="om-pay-card">
            {[
              ['Subtotal',                    formatCurrency(order.subtotal),              false],
              ...(order.taxAmount > 0 ? [['Tax',           formatCurrency(order.taxAmount), false]] : []),
              ...(order.addon > 0 ? [['Addon',             `+ ${formatCurrency(order.addon)}`, false]] : []),
              ...(order.deliveryCharge > 0 ? [['Delivery', `+ ${formatCurrency(order.deliveryCharge)}`, false]] : []),
              ...(order.discount > 0 ? [['Manual Discount', `− ${formatCurrency(order.discount)}`, false]] : []),
              ...(order.subscriptionDiscount > 0 ? [['Subscription Discount', `− ${formatCurrency(order.subscriptionDiscount)}`, false]] : []),
            ].map(([label, val]) => (
              <div key={label} className="om-pay-row">
                <span>{label}</span><span>{val}</span>
              </div>
            ))}
            <div className="om-pay-divider" />
            <div className="om-pay-total-row">
              <span>Grand Total</span>
              <span>{formatCurrency(order.totalAmount)}</span>
            </div>
            {order.walletAmountUsed > 0 && (
              <div className="om-pay-row accent-green">
                <span>💰 Wallet Applied</span>
                <span>− {formatCurrency(order.walletAmountUsed)}</span>
              </div>
            )}
            {order.creditAmountUsed > 0 && (
              <div className="om-pay-row accent-amber">
                <span>🏦 On Credit</span>
                <span>{formatCurrency(order.creditAmountUsed)}</span>
              </div>
            )}
            <div className="om-pay-final-row">
              <span>Amount Collected</span>
              <span>{formatCurrency(order.finalAmount)}</span>
            </div>
          </div>
        </div>

        {/* Subscription info if applicable */}
        {hasSub && (
          <div className="om-detail-section">
            <div className="om-section-title">Subscription Applied</div>
            <div className="om-sub-card">
              <div className="om-sub-icon">⭐</div>
              <div>
                <div className="om-sub-name">{order.subscriptionApplied?.subscriptionPlan?.name || 'Subscription Plan'}</div>
                {order.subscriptionDiscount > 0 && (
                  <div className="om-sub-detail">Discount applied: {formatCurrency(order.subscriptionDiscount)}</div>
                )}
                {order.bundleItemsUsed > 0 && (
                  <div className="om-sub-detail">Bundle items used: {order.bundleItemsUsed}</div>
                )}
                <div className="om-sub-detail" style={{ color: '#94a3b8' }}>
                  Payment: {order.paymentMethod === 'subscription_credit' ? 'Subscription Credit (no charge)' : 'Normal'}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default OrderManagement;
